import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ReferenceLine, ResponsiveContainer, LabelList } from 'recharts';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      delay: i * 0.12,
      ease: 'easeOut'
    }
  })
};
const data = [{
  period: 'Mo 1',
  reps: 15
}, {
  period: 'Mo 2',
  reps: 30
}, {
  period: 'Mo 3',
  reps: 45
}, {
  period: 'Mo 4',
  reps: 60
}, {
  period: 'Mo 5',
  reps: 75
}, {
  period: 'Mo 6',
  reps: 90
}, {
  period: 'Mo 7',
  reps: 105
}, {
  period: 'Mo 8',
  reps: 120
}, {
  period: 'Mo 9',
  reps: 135
}, {
  period: 'Mo 10',
  reps: 150
}, {
  period: 'Mo 11',
  reps: 165
}, {
  period: 'Mo 12',
  reps: 180
}];
const MARKER_FONT = "'Permanent Marker', cursive";
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-[#1A2B4A] border border-[#4ECDC4]/30 rounded-xl p-3 shadow-lg text-sm" data-id="element-528">
        <p className="font-bold text-white mb-1" data-id="element-529">{label}</p>
        <p className="text-[#4ECDC4] font-medium" data-id="element-530">
          {payload[0].value.toLocaleString()} Cumulative Hires
        </p>
      </div>;
  }
  return null;
};
const CustomLabel = ({
  x,
  y,
  width,
  value,
  index
}: any) => {
  if (index !== 5) return null;
  const cx = x + width / 2;
  return <g data-id="element-531">
      {/* Marker-style annotation — no rotation, raised higher */}
      <text x={cx} y={y - 38} fill="white" textAnchor="middle" fontSize={13} fontFamily={MARKER_FONT} data-id="element-532">
        90 hires! ★
      </text>
      {/* Dashed arrow pointing down to bar */}
      <line x1={cx} y1={y - 28} x2={cx} y2={y - 7} stroke="white" strokeWidth={1.5} strokeDasharray="2,2" opacity={0.65} data-id="element-533" />
      <polygon points={`${cx - 3},${y - 9} ${cx + 3},${y - 9} ${cx},${y - 3}`} fill="white" opacity={0.65} data-id="element-534" />
    </g>;
};
export function RepsOverTimeSection() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.2
  });
  return <section className="relative w-full bg-[#1A2B4A] grid-texture py-24 px-6 overflow-hidden" data-id="element-535">
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-[#4ECDC4]/6 rounded-full blur-[120px] pointer-events-none" data-id="element-536" />

      <div ref={ref} className="max-w-5xl mx-auto relative z-10" data-id="element-537">
        <motion.div custom={0} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="text-center mb-14" data-id="element-538">
          <span className="inline-block bg-[#4ECDC4] text-[#1A2B4A] text-xs font-bold uppercase px-3 py-1 rounded-full mb-4 tracking-wider" data-id="element-539">
            Scale Over Time
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 leading-tight" data-id="element-540">
            The System at Scale
          </h2>
          <p className="text-white/60 text-lg max-w-xl mx-auto" data-id="element-541">
            Cumulative hires — 15 new hires added every month.
          </p>
        </motion.div>

        <motion.div custom={1} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="bg-white/10 backdrop-blur-sm border border-[#4ECDC4]/30 rounded-2xl p-6 sm:p-8 mb-10" data-id="element-542">
          <ResponsiveContainer width="100%" height={300} data-id="element-543">
            <BarChart data={data} margin={{
            top: 56,
            right: 16,
            left: 0,
            bottom: 5
          }} barCategoryGap="20%" data-id="element-544">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(78,205,196,0.1)" vertical={false} data-id="element-545" />
              <XAxis dataKey="period" tick={{
              fill: 'rgba(255,255,255,0.6)',
              fontSize: 10
            }} axisLine={false} tickLine={false} data-id="element-546" />
              <YAxis tick={{
              fill: 'rgba(255,255,255,0.5)',
              fontSize: 11
            }} tickFormatter={v => `${v}`} axisLine={false} tickLine={false} domain={[0, 200]} ticks={[0, 30, 60, 90, 120, 150, 180]} data-id="element-547" />
              <Tooltip content={<CustomTooltip data-id="element-549" />} cursor={{
              fill: 'rgba(78,205,196,0.05)'
            }} data-id="element-548" />
              <Bar dataKey="reps" radius={[5, 5, 0, 0]} animationDuration={inView ? 1500 : 0} data-id="element-550">
                {data.map((entry, index) => <Cell key={`cell-${index}`} fill={index === 5 ? '#4ECDC4' : `rgba(78, 205, 196, ${0.4 + index / 11 * 0.4})`} stroke={index === 5 ? '#4ECDC4' : 'none'} strokeWidth={index === 5 ? 2 : 0} style={index === 5 ? {
                filter: 'drop-shadow(0 0 8px rgba(78,205,196,0.7))'
              } : {}} data-id="element-551" />)}
                <LabelList content={<CustomLabel data-id="element-553" />} data-id="element-552" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Milestone callouts */}
          <div className="grid grid-cols-3 gap-3 mt-6 pt-6 border-t border-[#4ECDC4]/15" data-id="element-554">
            <div className="text-center" data-id="element-555">
              <p className="text-lg font-black text-[#4ECDC4]" data-id="element-556">15</p>
              <p className="text-xs text-white/50" data-id="element-557">New Hires / Month</p>
            </div>
            <div className="text-center" data-id="element-558">
              <p className="text-lg font-black text-[#4ECDC4]" data-id="element-559">90 ★</p>
              <p className="text-xs text-white/50" data-id="element-560">Hires at 6 Months</p>
            </div>
            <div className="text-center" data-id="element-561">
              <p className="text-lg font-black text-[#4ECDC4]" data-id="element-562">180</p>
              <p className="text-xs text-white/50" data-id="element-563">Hires at 12 Months</p>
            </div>
          </div>
        </motion.div>

        <motion.div custom={2} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="bg-white/10 backdrop-blur-sm border border-[#4ECDC4]/30 rounded-2xl p-6 text-center" data-id="element-564">
          <p className="text-white font-semibold text-lg" data-id="element-565">
            Built in our own D2D company.{' '}
            <span className="text-[#4ECDC4]" data-id="element-566">Now installed in yours.</span>
          </p>
        </motion.div>
      </div>
    </section>;
}